import type { ComputedRef, InjectionKey, Ref } from 'vue'
import { computed, ref } from 'vue'

export interface CartItem {
  productId: string
  productCategoryId?: string
  productName: string
  variantId: string
  variantName?: string
  count: number
  maximum?: number
  price: number
  deliveryFee?: number
  comment?: string
  file?: File[]
  imageUrl?: string
}

export interface FestivalShopCart {
  // Cart State
  cartItems: Ref<CartItem[]>
  cartItemCount: ComputedRef<number>
  cartTotal: ComputedRef<number>
  // Cart Methods
  isInCart: (productId: string, variantId?: string) => boolean
  getCartItem: (productId: string, variantId: string) => CartItem | undefined
  numberInCart: (productId: string) => number
  addToCart: (item: CartItem) => void
  updateCartItemCount: (productId: string, variantId: string, count: number) => void
  removeFromCart: (productId: string, variantId: string) => void
  clearCart: () => void
}

export const FestivalShopCartKey: InjectionKey<FestivalShopCart> = Symbol('festivalShopCart')

const cartOpen = ref(false)
const cartItems = ref<CartItem[]>([])

export function useFestivalShopCart() {
  // Cart State
  const cartItemCount = computed(() => {
    return cartItems.value.reduce((sum, item) => sum + item.count, 0)
  })

  const cartTotal = computed(() => {
    return cartItems.value.reduce((sum, item) => sum + item.price * item.count + (item.deliveryFee || 0), 0)
  })

  const isCartOpen = computed(() => cartOpen.value)

  const isInCart = (productId: string, variantId?: string) => {
    if (variantId) {
      return cartItems.value.some((item) => item.productId === productId && item.variantId === variantId)
    }
    return cartItems.value.some((item) => item.productId === productId)
  }

  const numberInCart = (productId: string) => {
    return cartItems.value.filter((item) => item.productId === productId).reduce((sum, item) => sum + item.count, 0)
  }

  const getCartItem = (productId: string, variantId: string) => {
    return cartItems.value.find((item) => item.productId === productId && item.variantId === variantId)
  }

  const addToCart = (item: CartItem) => {
    const existingItemIndex = cartItems.value.findIndex((i) => i.productId === item.productId && i.variantId === item.variantId)
    const maximum = item.maximum ?? cartItems.value[existingItemIndex]?.maximum

    if (existingItemIndex >= 0) {
      const existingItem = cartItems.value[existingItemIndex]
      const updatedCount = maximum ? Math.min(existingItem.count + item.count, maximum) : existingItem.count + item.count
      // Update existing item
      cartItems.value[existingItemIndex] = {
        ...existingItem,
        count: updatedCount,
        maximum: maximum ?? existingItem.maximum,
        comment: item.comment || existingItem.comment,
        file: item.file || existingItem.file,
      }
    } else {
      // Add new item
      const limitedCount = item.maximum ? Math.min(item.count, item.maximum) : item.count
      cartItems.value.push({
        ...item,
        count: limitedCount,
      })
    }
  }

  const updateCartItemCount = (productId: string, variantId: string, count: number) => {
    const item = cartItems.value.find((i) => i.productId === productId && i.variantId === variantId)
    if (item) {
      if (count <= 0) {
        removeFromCart(productId, variantId)
      } else {
        item.count = item.maximum ? Math.min(count, item.maximum) : count
      }
    }
  }

  const removeFromCart = (productId: string, variantId: string) => {
    cartItems.value = cartItems.value.filter((item) => !(item.productId === productId && item.variantId === variantId))
  }

  const clearCart = () => {
    cartItems.value = []
  }

  const showCart = () => {
    cartOpen.value = true
  }

  const closeCart = () => {
    cartOpen.value = false
  }

  return {
    // Cart
    cartItems,
    cartItemCount,
    cartTotal,

    isCartOpen,
    showCart,
    closeCart,

    isInCart,
    numberInCart,
    getCartItem,
    addToCart,
    updateCartItemCount,
    removeFromCart,
    clearCart,
  }
}
